import _ from 'lodash';
import React, { ReactNode, useCallback, useState } from 'react';
import { generatePath, useHistory } from 'react-router';
import dayjs from 'dayjs';
import Select from 'react-select';

// Material UI
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

// Data
import { numToTime } from '../../../lib/helpers/conversion.helpers';
import { FormLabel, IFormOption } from '../../_core/_ui/forms.component';

// Components
import { JerseyIcon } from '../../Session/CheckIn/check-in.helpers';
import {
  JerseyIconList,
  JerseyIconWrapper,
  JerseyPlayerList,
  JerseyPlayerWrapper,
  StatusIcon
} from './manage-evaluations.helpers';
import { PageHeaderRow, SpacedRow, Spacer } from '../../_core/_ui/structure.components';
import { OutlineButton, PrimaryWhiteButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { FormControl, IconButton, Modal, Checkbox, FormControlLabel } from '@material-ui/core';
import { getAuthUser } from '../../../lib/services/auth.service';
import { ReviewEvaluations } from './review-evaluations.component';
import { useOnlineStatus, useSyncing } from '../../../lib/stores/connectivity.store';
import { hasAnyRoles } from '../../../lib/services/user.service';
import ImageIcon from '@material-ui/icons/Image';
import { SportTerminology } from '../../../lib/types';
import { RenderSkillWidget } from './render-skill-widget.component';
import { scoreCellKey } from './score-cell.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { getPlayerName } from '../../../lib/utils/player-name.service';
import { ROLES, STAFF_PATHS } from '../../../../_configuration';

interface IManageEvaluations {
  loading: boolean;
  session?: IAssessmentSession;
  jerseys: { [key: string]: IJersey };
  removeScore: Function;
  recordScore: Function;
  playersByPosition: { [key: string]: IPlayerAssessment[] };
  playersById: { [key: string]: IPlayerAssessment };
  playersLinear: IPlayerAssessment[];
  currPlayerIndex: number;
  setCurrPlayerIndex: Function;
  playerOptions: IFormOption[];
  drills: IDrill[];
  drillsById: { [key: string]: IDrill };
  drillOptions: IFormOption[];
  activePlayer?: IPlayerAssessment;
  setActivePlayer: Function;
  activeDrill?: IDrill;
  setActiveDrill: (drill: IDrill, round?: number) => void;
  activeRound: number;
  setActiveRound: Function;
  getScoreStatus: (playerAssessment: IPlayerAssessment, drillId: string, round: number) => string;
  scoreHash: { [key: string]: { [key: string]: { [key: string]: { [key: number]: IScore } } } };
  pendingScoreKeys?: ReadonlySet<string>;
  showReview: boolean;
  setShowReview: Function;
  canCheckIn: boolean;
  submitScores: Function;
  hasSaveError: boolean;
  hasUnsavedScores: boolean;
  isSaving: boolean;
}

export const ManageEvaluations: React.FC<IManageEvaluations> = ({
  loading,
  session,
  jerseys,
  removeScore,
  recordScore,
  playersByPosition,
  playersById,
  playersLinear,
  currPlayerIndex,
  playerOptions,
  drillsById,
  drillOptions,
  activePlayer,
  setActivePlayer,
  drills,
  activeDrill,
  setActiveDrill,
  activeRound,
  setActiveRound,
  getScoreStatus,
  scoreHash,
  pendingScoreKeys,
  showReview,
  setShowReview,
  canCheckIn,
  submitScores,
  hasUnsavedScores,
  hasSaveError,
  isSaving
}) => {
  const termLookup = useCompanySportTerminology();
  const athleteTerm = termLookup[SportTerminology.ATHLETE];
  const athletesTerm = pluralTerm(athleteTerm);
  const eventTerm = termLookup[SportTerminology.EVENT];

  const isOnline = useOnlineStatus();
  const isSyncing = useSyncing();
  const user = getAuthUser();
  const history = useHistory();
  const [showDiagram, setShowDiagram] = useState<string | null>(null);
  const hasPlayerNamePermission = hasAnyRoles({roles: [ROLES.PLAYER_NAMES], ageGroupId: session?.ageGroup._id, assessmentId: session?.assessmentId});
  const [showPlayerNames, setShowPlayerNames] = useState<boolean>(hasPlayerNamePermission);

  const renderJerseyList = useCallback(() => {
    if (_.isEmpty(playersByPosition))
      return <JerseyIconWrapper style={{ width: '100%', justifyContent: 'center' }} isLabel>No {athletesTerm} Checked-In</JerseyIconWrapper>

    const WrapperComponent = showPlayerNames ? JerseyPlayerWrapper : JerseyIconWrapper
    const jerseyList = _.map(playersByPosition, (players, positionName) => {
      return (
        <React.Fragment key={`jerseys_${positionName}`}>
          <JerseyIconWrapper isLabel>{positionName}</JerseyIconWrapper>
          {players.map((player) => (
            <WrapperComponent key={player._id} active={player._id === activePlayer?._id} onClick={() => setActivePlayer(player)}>
              <JerseyIcon color={jerseys[player._id]?.color.toLowerCase()}>{jerseys[player._id]?.number}</JerseyIcon>
              {showPlayerNames && <Box mx={2} textAlign="left"><Typography>{getPlayerName({player: player.player, firstNameFirst: true}).toUpperCase()}</Typography></Box>}
              {activeDrill && <StatusIcon status={getScoreStatus(player, activeDrill._id, activeRound)} />}
            </WrapperComponent>
          ))}
        </React.Fragment>
      )
    })

    if (showPlayerNames) return <JerseyPlayerList>{jerseyList}</JerseyPlayerList>
    return  <JerseyIconList style={{ top: (isOnline && !isSyncing) ? 0 : '48px' }}>{jerseyList}</JerseyIconList>
  }, [playersByPosition, athletesTerm, showPlayerNames, isOnline, isSyncing, activePlayer, jerseys, activeDrill, getScoreStatus, activeRound, setActivePlayer])

  if (loading && !session) return <Typography>Loading...</Typography>;
  if (!session) return <Typography>Unable to load evaluations at this time. Please try again later</Typography>;

  const eleSkills = renderSkills({
    activeDrill,
    activePlayer,
    activeRound,
    user,
    session,
    scoreHash,
    pendingScoreKeys,
    removeScore,
    recordScore
  });

  let scoreStatusBg = 'transparent';
  let scoreStatusColor = '#000';

  if (isSaving) {
    scoreStatusBg = '#fff';
  } else if (!isOnline && hasUnsavedScores && !isSaving) {
    scoreStatusBg = '#ed4337';
    scoreStatusColor = '#fff';
  } else if (isOnline && hasUnsavedScores && !isSaving) {
    scoreStatusBg = '#fff';
  }

  return (
    <div>
      {renderJerseyList()}

      <Box ml={showPlayerNames ? 30 : 0}>
        <Box mt={showPlayerNames ? 0 : 10} display="flex">
          <Box>
            <Typography variant="h6">{session.location} - {session.area}</Typography>
            <Typography variant="subtitle1">{session.ageGroup.name} | {dayjs(session.date).format('MMMM Do')} @ {numToTime(session.start)}</Typography>
          </Box>

          <Spacer />

          {hasPlayerNamePermission && (
            <Box mr={2} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showPlayerNames}
                    onChange={(e) => setShowPlayerNames(e.target.checked)}
                  />
                }
                label={`Show ${athleteTerm} Names`}
              />
            </Box>
          )}

          <SecondaryButton startIcon={<ArrowBackIosIcon />} onClick={() => history.push('/upcoming')}>Home</SecondaryButton>
          {canCheckIn && (
            <PrimaryWhiteButton
              style={{ marginLeft: '16px' }}
              onClick={() => history.push(generatePath(STAFF_PATHS.CHECK_IN, { assessmentSessionId: session._id }))}
            >Check-In {athletesTerm}</PrimaryWhiteButton>
          )}
          <PrimaryWhiteButton
            disabled={isSaving || !hasUnsavedScores || !isOnline}
            style={{ marginLeft: '16px', backgroundColor: scoreStatusBg, color: scoreStatusColor, width: '139px' }}
            onClick={() => submitScores()}
          >
            {isSaving && 'Saving...'}
            {isOnline && hasUnsavedScores && !isSaving && 'Submit Scores'}
            {!isOnline && hasUnsavedScores && !isSaving && 'No Internet'}
            {!hasUnsavedScores && !isSaving && 'Scores Saved'}
          </PrimaryWhiteButton>
        </Box>
        {hasSaveError && (
          <Box>
            <Typography variant="caption">An error occurred when saving. Please check your internet connection and try again</Typography>
          </Box>
        )}

        <Box my={2}>
          {/* Navigation here */}
          {!showReview && (
            <Paper>
              <PageHeaderRow style={{ marginBottom: -8, paddingLeft: 16, paddingRight: 16, paddingTop: 16 }}>
                {
                  showPlayerNames && activePlayer
                    ? <Typography variant="h6">{getPlayerName(activePlayer)}</Typography>
                    : <div />
                }
              </PageHeaderRow>
              <SpacedRow>
                <JerseyIcon color={activePlayer && jerseys[activePlayer._id]?.color?.toLowerCase()}>{activePlayer && jerseys[activePlayer._id]?.number}</JerseyIcon>
                <FormControl style={{ width: '142px' }}>
                  <FormLabel>Active {athleteTerm}</FormLabel>
                  <Select
                    loading={!activePlayer}
                    options={playerOptions}
                    value={activePlayer && { value: activePlayer._id, label: `${_.capitalize(jerseys[activePlayer._id]?.color)} - ${jerseys[activePlayer._id]?.number}` }}
                    isSearchable={false}
                    isOptionDisabled={option => option.value === activePlayer?._id}
                    onChange={option => option && setActivePlayer(playersById[option.value])}
                    noOptionsMessage={() => `No ${athletesTerm} Assigned`}
                  />
                </FormControl>
                <FormControl style={{ width: '310px' }}>
                  <FormLabel>Active {eventTerm}</FormLabel>
                  <Select
                    options={drillOptions}
                    loading={!activeDrill}
                    value={activeDrill && { value: activeDrill._id, label: activeDrill.name }}
                    isSearchable={false}
                    isOptionDisabled={option => option.value === activeDrill?._id}
                    onChange={option => option && setActiveDrill(drillsById[option.value])}
                    noOptionsMessage={() => `No ${pluralTerm(eventTerm)} Assigned`}
                  />
                </FormControl>
                {activeDrill?.image && <IconButton style={{ marginTop: '20px' }} onClick={() => setShowDiagram(activeDrill.image)}>
                  <ImageIcon />
                </IconButton>}
                <FormControl style={{ width: '120px' }}>
                  <FormLabel>Active Round</FormLabel>
                  <Select
                    options={Array(5).fill(undefined).map((_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
                    value={{ value: String(activeRound), label: String(activeRound) }}
                    isSearchable={false}
                    isOptionDisabled={option => Number(option.value) === activeRound}
                    onChange={option => option && setActiveRound(Number(option.value))}
                  />
                </FormControl>
                <Spacer />
                <SecondaryButton
                  disabled={currPlayerIndex === 0}
                  onClick={() => setActivePlayer(playersLinear[currPlayerIndex - 1])}
                >Previous {athleteTerm}</SecondaryButton>
                <SecondaryButton
                  disabled={currPlayerIndex === playersLinear.length - 1}
                  onClick={() => setActivePlayer(playersLinear[currPlayerIndex + 1])}
                >Next {athleteTerm}</SecondaryButton>
                <OutlineButton onClick={() => setShowReview(!showReview)}>Review Scores</OutlineButton>
              </SpacedRow>
              <Divider />
              {activeDrill && (
                <Box p={2}>
                  <Typography variant="h6" gutterBottom>{activeDrill.name}</Typography>
                  {eleSkills}
                  {_.isArray(eleSkills) && eleSkills.length === 0 && activePlayer && <Typography>{eventTerm} does not apply to this {athleteTerm}'s position</Typography>}
                  {!activePlayer && <Typography>No {athleteTerm} selected</Typography>}
                </Box>
              )}
              {!activeDrill && (
                <Box p={2}><Typography>No {eventTerm.toLowerCase()} selected</Typography></Box>
              )}
            </Paper>
          )}

          {showReview && (
            <Paper>
              <Box p={2} display="flex">
                <Typography variant="h6">Review Scores</Typography>
                <Spacer />
                <OutlineButton onClick={() => setShowReview(false)}>Score {athletesTerm}</OutlineButton>
              </Box>
              <ReviewEvaluations
                players={_.values(playersById)}
                jerseys={jerseys}
                drills={drills}
                scoreHash={scoreHash}
                setActiveDrill={setActiveDrill}
                setActivePlayer={setActivePlayer}
                setShowReview={setShowReview}
              />
            </Paper>
          )}

          <Modal
            open={Boolean(showDiagram)}
            onClose={() => setShowDiagram(null)}
            className="diagram-modal"
          >
            <Paper style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
              <Box p={2}>
                {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                {showDiagram && <img src={showDiagram} style={{ minWidth: '400px', maxWidth: '100%' }} alt="Broken Image. Please re-upload" />}
              </Box>
            </Paper>
          </Modal>
        </Box>
      </Box>
    </div>
  );
};

interface IRenderSkills {
  activeDrill?: IDrill;
  activePlayer?: IPlayerAssessment;
  activeRound: number;
  user: IUser | null;
  scoreHash: { [key: string]: { [key: string]: { [key: string]: { [key: number]: IScore } } } };
  pendingScoreKeys?: ReadonlySet<string>;
  session: IAssessmentSession;
  removeScore: Function;
  recordScore: Function;
}

const renderSkills = ({
  activeDrill,
  activePlayer,
  activeRound,
  user,
  session,
  scoreHash,
  pendingScoreKeys,
  removeScore,
  recordScore
}: IRenderSkills) => {
  const eleSkills: ReactNode[] = [];

  if (activeDrill && activePlayer && user) {
    activeDrill.skillSets.forEach((skillSet: ISkillSet) => {
      if (_.includes(skillSet.positionIds, activePlayer.position._id)) {
        skillSet.skills?.forEach((skill, index) => {
          if (!skill.deletedAt) {
            const context: any = {
              drillId: activeDrill._id,
              skillId: skill._id,
              assessmentSessionId: session._id,
              playerAssessmentId: activePlayer._id,
              assessmentId: session.assessmentId,
              ageGroupId: activePlayer.ageGroup._id,
              round: activeRound
            };

            const currScore = _.get(scoreHash, [activePlayer._id, activeDrill._id, skill._id, activeRound]);
            if (currScore) {
              context._id = currScore._id;
            }


            eleSkills.push(<RenderSkill
              key={`${activeDrill._id}_${skill._id}`}
              context={context}
              score={currScore}
              pending={pendingScoreKeys?.has(scoreCellKey(context))}
              skill={skill}
              activeRound={activeRound}
              recordScore={recordScore}
              removeScore={removeScore}
            />);
            }
        });
      }
    });
  }

  return eleSkills;
}

interface IRenderSkill {
  context: {  [key: string]: any };
  skill: ISkill;
  score?: IScore;
  pending?: boolean;
  activeRound: number;
  recordScore: Function;
  removeScore: Function;
}

const RenderSkill: React.FC<IRenderSkill> = ({
  context,
  skill,
  score,
  pending,
  activeRound,
  recordScore,
  removeScore
}) => {
  return (
    <Box style={{ borderBottom: '1px solid #ddd' }} mb={1} pb={1}>
      <Typography key={`${skill._id}_title`} gutterBottom>{skill.name}</Typography>
      <RenderSkillWidget
        skill={skill}
        score={score}
        pending={pending}
        roundNum={activeRound}
        onRemove={(s) => removeScore(s)}
        onSave={(raw, type) => recordScore({ ...context, raw, type, clientId: score?.clientId })}
      />
    </Box>
  );
}
